import { FC, memo, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import HeartIcon from '@heroicons/react/24/solid/HeartIcon';
import { Cart } from 'shared/types/cart';
import { Money, Product, Variant } from 'shared/types/product';
import { ProductCardProduct } from 'shared/types/product/Product';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { RouteHelpers } from 'helpers/routerHelpers';
import { amplienceImageLoader } from 'helpers/utils/amplience/imageLoader';
import { cn } from 'helpers/utils/classnames';
import { gtmSelectItem } from 'helpers/utils/gtm-events/gtmEccomerce';
import { LineItemMapper } from 'helpers/utils/gtm-events/mapper/LineItemMapper';
import { desktop, tablet } from 'helpers/utils/screensizes';
import ForwardIcon from 'icons/forward';
import { useAccount, useCart } from 'frontastic/hooks';
import { gtmTrackEvent } from '../../../../../helpers/utils/gtm-events/gtmTrackEvent';
import {
  getInStockVariantWithHighestSaleScore,
  sortVariantsBySaleScore,
} from '../../../../../helpers/utils/product/variants';
import ProductColorTag from '../../details/components/ProductColorTag';

interface ProductListCardProps {
  product: ProductCardProduct;
  location?: 'Recently Viewed' | 'You May Also Like' | 'Shop Collection';
  defaultColor?: string;
  onDLSelectItemOveride?: (d: object) => void;
  selectProduct: (product: Product) => void;
  selectProductVariant?: (variant: Variant | undefined) => void;
  openModal: () => void;
  isShopCollection?: boolean;
  isReadyToShipActive?: boolean;
}

const getDiscountPrices = (variant: Variant[]) => {
  const discountedPrices = new Set([
    ...variant.filter((variant) => variant.discountedPrice).map((variant) => variant.discountedPrice!.centAmount!),
  ]);

  // If there is no variant that has a discount
  if (discountedPrices.size === 0) {
    return null;
  }

  // If there is only one variant that has a discount
  if (discountedPrices.size === 1) {
    const discountedMin: Money = {
      centAmount: Array.from(discountedPrices)[0],
      currencyCode: 'USD',
      fractionDigits: 2,
    };

    return { discountedMin };
  }

  // If there are more than 1 variant that has a discount
  const discountedMin: Money = { centAmount: Math.min(...discountedPrices), currencyCode: 'USD', fractionDigits: 2 };
  const discountedMax: Money = { centAmount: Math.max(...discountedPrices), currencyCode: 'USD', fractionDigits: 2 };

  return { discountedMin, discountedMax };
};

const ProductListCard: FC<ProductListCardProps> = ({
  product,
  location,
  onDLSelectItemOveride,
  defaultColor,
  openModal,
  selectProduct,
  selectProductVariant,
  isShopCollection,
  isReadyToShipActive = false,
}) => {
  const { account } = useAccount();
  const { data: cart } = useCart();

  const searchParams = useSearchParams();

  const filterColorQueryParams = isShopCollection ? searchParams.get('color') : searchParams.get('filterColor');
  const filterFinishQueryParams = searchParams.get('filterFinish');

  const defaultVariant = product.matchingVariants?.matchedVariants?.[0]
    ? product.variants?.find((variant) => variant.sku === product.matchingVariants?.matchedVariants?.[0]?.sku)
    : undefined;

  const [selectedVariant, setSelectedVariant] = useState<Variant | undefined>(undefined);

  const [isDesktop] = useMediaQuery(desktop, true);

  const [isTablet] = useMediaQuery(tablet, false);

  const groupedImagesByColor = product.variants.reduce((acc: { [color: string]: string }, variant) => {
    const color = variant.attributes?.actualColor;
    const image = variant.swatchImage;

    if (!acc[color] && image) {
      acc[color] = image;
    }
    return acc;
  }, {});

  const colors = Object.keys(groupedImagesByColor);

  const itemsPerPageMobile = isTablet ? 3 : 2;

  const itemsPerPage = isDesktop ? 4 : itemsPerPageMobile;

  const indexOfSelectedVariant = product.variants.findIndex((variant) => variant.sku === selectedVariant?.sku);

  const startIndex = Math.max(indexOfSelectedVariant - Math.floor(itemsPerPage / 2), 0);

  const colorsToShow = isDesktop ? colors.slice(0, 4) : colors.slice(startIndex, startIndex + itemsPerPage);

  // customVariant makes a copy of the original to prevent to sort the original one
  const customVariant = [...product.variants];
  const minPrice = customVariant.sort((a, b) => a.price?.centAmount! - b.price?.centAmount!)[0].price!;

  const maxPrice = customVariant.sort((a, b) => b.price?.centAmount! - a.price?.centAmount!)[0].price!;

  const discountedPrices = getDiscountPrices(product.variants!);

  const originalPrice = selectedVariant?.originalPrice;

  const remainColorVariantsLength =
    Object.keys(groupedImagesByColor).length > 4 ? Object.keys(groupedImagesByColor).length - 4 : 0;

  const currentColor = (selectedVariant?.attributes || product.masterVariant?.attributes)?.actualColor;

  const filterFinishKey = (selectedVariant?.attributes || product.masterVariant?.attributes)?.filterFinish?.[0]?.key;

  const cardUrl =
    selectedVariant && currentColor
      ? product._url +
        RouteHelpers.encodeToURL({ color: currentColor, filterFinish: filterFinishQueryParams ?? filterFinishKey })
      : product._url;

  const handleNext = () => {
    if (indexOfSelectedVariant < colors.length) {
      setSelectedVariant(product.variants[indexOfSelectedVariant + 1]);
    }
  };

  const handlePrev = () => {
    if (indexOfSelectedVariant > 0) {
      setSelectedVariant(product.variants[indexOfSelectedVariant - 1]);
    }
  };

  useEffect(() => {
    let variantToUse: Variant | undefined;

    if (isShopCollection) {
      const matchedVariant = defaultColor
        ? product.variants.find((variant) => variant.attributes?.actualColor === defaultColor)
        : undefined;

      variantToUse = matchedVariant || product.masterVariant;
    } else if (filterColorQueryParams || filterFinishQueryParams) {
      const filterColor = filterColorQueryParams;
      const filterFinished = filterFinishQueryParams;

      const selectedVariant = product.variants.find((variant) => {
        const matchesColor = filterColor
          ? variant.attributes?.filterColor?.some((color: { key: string; label: string }) => color.key === filterColor)
          : true;
        const matchesFinish = filterFinished
          ? variant.attributes?.filterFinish?.some(
              (finish: { key: string; label: string }) => finish.key === filterFinished,
            )
          : true;

        return matchesColor && matchesFinish;
      });

      variantToUse = selectedVariant || defaultVariant;
    } else {
      const defaultVariant = product.matchingVariants?.matchedVariants?.[0]
        ? product.variants?.find((variant) => variant.sku === product.matchingVariants?.matchedVariants?.[0]?.sku)
        : undefined;

      const defaultVariantBasedOnDefaultColor = product.variants.find((variant) =>
        variant?.attributes?.filterColor?.some((color: { key: string; label: string }) => color.key === defaultColor),
      );

      const sortedVariants = sortVariantsBySaleScore(product.variants);
      const defaultVariantBasedOnSaleScore = sortedVariants[0];

      const highPriorityVariant = defaultVariantBasedOnDefaultColor ?? defaultVariantBasedOnSaleScore;

      variantToUse = highPriorityVariant ?? defaultVariant;
    }

    if (isReadyToShipActive) {
      variantToUse = getInStockVariantWithHighestSaleScore(product.masterVariant, product.variants);
    }

    setSelectedVariant(variantToUse);
  }, [
    defaultColor,
    product,
    filterColorQueryParams,
    filterFinishQueryParams,
    isReadyToShipActive,
    isShopCollection,
    defaultVariant,
  ]);

  if (!selectedVariant) {
    return <Skeleton className="h-[550px] w-full rounded-lg bg-primary-lightcream" />;
  }

  return (
    <>
      <Link
        onClick={() => {
          if (onDLSelectItemOveride) {
            onDLSelectItemOveride({
              itemsDataLayer: LineItemMapper.fromProductToItemDataLayer(product, 1, product.masterVariant!),
              account,
              cart,
            });
          } else {
            gtmSelectItem({
              item_list_name: location,
              itemsDataLayer: LineItemMapper.fromProductToItemDataLayer(product, 1, product.masterVariant!),
              account,
              cart,
            });
          }
        }}
        href={cardUrl || ''}
        className="relative flex size-full"
        aria-label={`Product ${product.name}`}
      >
        <div className="size-full rounded-lg bg-primary-lightcream">
          <div className="group relative w-full justify-center pt-[100%]">
            <Image
              fill
              loader={amplienceImageLoader}
              aria-label={`Image of the product ${product.name} with ${currentColor} color`}
              alt={`A ${product.name} with ${currentColor} color`}
              src={selectedVariant?.images?.[0] || product.masterVariant?.images?.[0] || ''}
              loading="lazy"
              sizes="(max-width: 1024px) 50vw, 25vw"
              className="rounded-t-[6px] object-cover"
            />
            {isDesktop && (
              <QuickViewButton
                product={product}
                defaultVariant={selectedVariant}
                location={location}
                cart={cart}
                onDLSelectItemOveride={onDLSelectItemOveride}
                openModal={openModal}
                selectProduct={selectProduct}
                selectVariant={selectProductVariant}
              />
            )}
          </div>
          <div className="px-2 pb-3 pt-[10px] sm:px-5">
            <DebugOptions product={product} />
            <div className="flex h-8 lg:overflow-visible">
              {Object.keys(groupedImagesByColor).length > 1 && (
                <>
                  {!isDesktop && colors.length > 3 && (
                    <Button
                      aria-label="Previous"
                      className="rotate-180 px-2 disabled:opacity-30"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePrev();
                      }}
                      disabled={indexOfSelectedVariant <= 0}
                    >
                      <ForwardIcon />
                    </Button>
                  )}
                  <ul className="mx-auto flex w-max items-center gap-4 px-2">
                    {colorsToShow.map((color, idx) => (
                      <ProductColorTag
                        key={`product-list-card-${product.name}-${color}`}
                        color={color}
                        imageUrl={groupedImagesByColor[color]}
                        isDisabled={false}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          gtmTrackEvent('plp_interactions', { click_action: 'colour selection' });
                          const targetVariant = product.variants.find(
                            (variant) => variant.attributes?.actualColor === color,
                          );
                          if (!targetVariant) return;
                          setSelectedVariant(targetVariant);
                        }}
                        isQuickView={false}
                        isSelectedVariant={color === selectedVariant?.attributes?.actualColor}
                      />
                    ))}
                    {!!remainColorVariantsLength && isDesktop && (
                      <Button className="text-sol-300 flex items-center justify-center text-sm font-normal leading-[17px]">
                        +{remainColorVariantsLength}
                      </Button>
                    )}
                  </ul>
                  {!isDesktop && colors.length > 3 && (
                    <Button
                      aria-label="Next"
                      onClick={(e) => {
                        e.preventDefault();
                        handleNext();
                      }}
                      className="cursor-pointer px-2 disabled:opacity-30"
                      disabled={indexOfSelectedVariant + 1 >= colors.length}
                    >
                      <ForwardIcon />
                    </Button>
                  )}
                </>
              )}
            </div>
            <p className="text-sol-300 mb-3 mt-[10px] line-clamp-2 inline-block h-[62px] min-h-[42px] w-full cursor-pointer text-center text-base font-semibold leading-5">
              {product.name}
            </p>
            <div
              className={cn('flex justify-center gap-2', {
                'max-sm:flex-col max-sm:items-center': maxPrice !== minPrice && (!!discountedPrices || originalPrice),
              })}
            >
              {!!discountedPrices ? (
                <strong
                  data-testid={`${product.productKey}-discountedPrice`}
                  className="font-semibold leading-5 text-red-500"
                >
                  {CurrencyHelpers.formatForCurrency(discountedPrices?.discountedMin)}{' '}
                  {discountedPrices.discountedMax &&
                    discountedPrices.discountedMin !== discountedPrices.discountedMax && (
                      <>- {CurrencyHelpers.formatForCurrency(discountedPrices.discountedMax)}</>
                    )}
                </strong>
              ) : (
                originalPrice && (
                  <strong
                    data-testid={`${product.productKey}-originalPrice`}
                    className="font-semibold leading-5 text-red-500"
                  >
                    {CurrencyHelpers.formatForCurrency({
                      centAmount: selectedVariant.price?.centAmount || 0,
                      currencyCode: 'USD',
                      fractionDigits: 2,
                    })}
                  </strong>
                )
              )}
              <strong
                className={cn('whitespace-nowrap font-semibold leading-5', {
                  'line-through': discountedPrices || originalPrice,
                })}
                data-testid={`${product.productKey}-price`}
              >
                {CurrencyHelpers.formatForCurrency(originalPrice ? originalPrice : minPrice)}{' '}
                {maxPrice !== minPrice && <>- {CurrencyHelpers.formatForCurrency(maxPrice)}</>}
              </strong>
            </div>
          </div>
        </div>
      </Link>
    </>
  );
};

function DebugOptions({ product }: { product: ProductCardProduct }) {
  const [isMounted, setIsMounted] = useState<boolean>(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) return null;
  return (
    localStorage.getItem('DEBUG') && (
      <div className="flex justify-start gap-2">
        <div className="size-6 text-accent-red">{product.isPinned ? <HeartIcon /> : ''}</div>
        <p className="rounded-full bg-green-800 p-1 text-center text-sm font-semibold text-white">
          {product.masterVariant?.attributes?.saleScore || 0}
        </p>
      </div>
    )
  );
}

const QuickViewButton = ({
  product,
  defaultVariant,
  location,
  cart,
  onDLSelectItemOveride,
  openModal,
  selectProduct,
  selectVariant,
}: {
  product: Product;
  defaultVariant?: Variant;
  location?: 'Recently Viewed' | 'You May Also Like' | 'Shop Collection';
  cart?: Cart;
  onDLSelectItemOveride?: (d: object) => void;

  selectProduct: (product: Product) => void;
  selectVariant?: (variant: Variant | undefined) => void;
  openModal: () => void;
}) => {
  const { account } = useAccount();

  return (
    <div>
      <div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          if (onDLSelectItemOveride) {
            onDLSelectItemOveride({
              itemsDataLayer: LineItemMapper.fromProductToItemDataLayer(product, 1, product.masterVariant!),
              cart,
              account,
            });
          } else {
            gtmSelectItem({
              itemsDataLayer: LineItemMapper.fromProductToItemDataLayer(product, 1, product.masterVariant!),
              item_list_name: location,
              cart,
              account,
            });
          }

          openModal();
          selectProduct(product);
          selectVariant && selectVariant(defaultVariant);
        }}
        className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 bg-white px-2 py-1 font-semibold text-[#222] hover:bg-black hover:text-white group-hover:block"
      >
        Quick View <span className="sr-only"> for {product.name}</span>
      </div>
    </div>
  );
};

export default memo(ProductListCard);
